/**
 * useApplicationForm — the full multi-step application form (non-easy-apply).
 * Mirrors Flutter CandidateFormBloc (lib/application/candidate_form/candidate_form_bloc.dart)
 * and the step widgets in lib/presentation/web_candidate_application_form/.
 *
 * Steps: 0 Resume → 1 Personal Information → 2 Education & Experience → 3 Additional Information
 *        → 4 Integrity Questionnaire (only when the tenant preference `questionInfo` exists).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@/app/router/routes';
import { useTenantStore } from '@/store/tenantStore';
import { useAppStateStore } from '@/store/appStateStore';
import { useLocalizationStore } from '@/store/localizationStore';
import { getUserDetails, getApplyApplicationFormContent } from '@/services/api/authApi';
import {
  getMasterData,
  getPartialApplicationFormData,
  getPlaceSuggestions,
  uploadResumeForApplication,
  saveApplication,
  downloadResume,
  fetchMasterDataFromResume,
  EMPTY_FILE_ERROR,
} from '@/services/api/applicationApi';
import { getJobDetails } from '@/services/api/homeApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import { validateResumeFile } from '@/features/jobs/utils/resumeFile';
import {
  getApplicationPageFields,
  getJobReferralData,
  setDefaultApplicationOrigin,
} from '@/features/jobs/utils/jobOrigin';
import {
  parseApplicationFormJson,
  parseComponentJson,
  populateComponentFields,
  getMonthsToYearArray,
  calculateExperienceInMonths,
  validateFormField,
  isDateOrderError,
  fieldPayloadValue,
  isQuestionVisible,
  formatDateForDisplay,
  applyGenderMandatoryInCountries,
} from '../utils/formHelpers';
import type { ParsedApplication, ValidationContext } from '../utils/formHelpers';
import type { ProfileInput, FldListState, ComponentState } from '@/types/application.types';
import { ApplicationStatus, ClickOptions, FieldType } from '@/types/application.types';

/* ── Steps ── */
export const STEPS = {
  RESUME: 0,
  PERSONAL_INFO: 1,
  EDUCATION_EXPERIENCE: 2,
  ADDITIONAL_INFO: 3,
  QUESTION_INFO: 4,
} as const;

/** Flutter WebCandidateApplicationFormConstants step titles */
export const STEP_LABELS = [
  'Resume',
  'Personal Information',
  'Education & Experience',
  'Additional Information',
  'Integrity Questionnaire',
];

/* ── Flutter string constants (English; Toast translates them) ── */
export const FILL_ALL_MANDATORY = 'some mandatory fields are either not filled or selected';
const CONSENT_REQUIRED = 'Please select the checkbox';
export const GENERIC_ERROR = 'Something went wrong, Please try again!';
/** Flutter: + Add is ignored once a list has more than 15 entries */
export const MAX_REPEATING_ENTRIES = 16;

const SALARY_KEYS = ['currentFixedSalary', 'currentVariableSalary', 'currentGrossSalary', 'currentExpectedSalary'];

export type SectionKey =
  | 'personalInformation'
  | 'address'
  | 'professionalDetails'
  | 'professionalDetailsNonIndia'
  | 'additionalInfo'
  | 'questionInfo'
  | 'education'
  | 'experience';

/** A field in the form: section, field index and (for education / experience) the entry index. */
export interface FieldRef {
  section: SectionKey;
  index: number;
  entry?: number;
}

export const errorKey = (ref: FieldRef) => `${ref.section}.${ref.entry ?? ''}.${ref.index}`;

/**
 * Validation context of a field: Flutter passes listOfComp (so dates are compared with the other
 * date of the entry) only for education and experience entries.
 */
export function validationContextFor(section: SectionKey, component: ComponentState): ValidationContext {
  return section === 'education' || section === 'experience' ? { siblings: component.fields } : {};
}

/**
 * Flutter upload failure text (candidate_form_bloc _UploadResume `l ?? failedToUploadFile`): the
 * repository's `l` is ErrorConstants.emptyFile on 204 and ErrorConstants.genericError otherwise.
 */
export function uploadFailureMessage(err: unknown): string {
  return err instanceof Error && err.message === EMPTY_FILE_ERROR ? EMPTY_FILE_ERROR : GENERIC_ERROR;
}

interface Toast {
  message: string;
  type: 'success' | 'error';
}

export interface ApplicationFormState {
  isInitializing: boolean;
  isSaving: boolean;
  isUploading: boolean;
  activeStep: number;
  application: ParsedApplication | null;
  profileInput: ProfileInput;
  candidateId: string;
  primaryEmailId: string;
  resumeUrl: string;
  resumeUploaded: boolean;
  selectedFile: File | null;
  uploadProgress: number;
  isLoadingResumeData: boolean;
  isIndia: boolean;
  selectedCountry: string;
  appliedStatus: string;
  showContinuePopup: boolean;
  /** Flutter additionalDetailsCheck — the certification checkbox */
  consentChecked: boolean;
  errors: Record<string, string>;
  toast: Toast | null;
  /** Tenant preference `questionInfo` enables the Integrity Questionnaire step. */
  showQuestionInfo: boolean;
}

/* ────────────────────────────── Pure helpers ────────────────────────────── */

type Templates = { education?: Record<string, unknown>; experience?: Record<string, unknown> };

function getComponent(app: ParsedApplication, section: SectionKey, entry?: number): ComponentState {
  if (section === 'education' || section === 'experience') return app[section][entry ?? 0];
  return app[section];
}

function withComponent(
  app: ParsedApplication,
  section: SectionKey,
  entry: number | undefined,
  update: (c: ComponentState) => ComponentState,
): ParsedApplication {
  if (section === 'education' || section === 'experience') {
    const list = app[section].map((c, i) => (i === (entry ?? 0) ? update(c) : c));
    return { ...app, [section]: list };
  }
  return { ...app, [section]: update(app[section]) };
}

function withFields(
  component: ComponentState,
  update: (f: FldListState, i: number) => FldListState,
): ComponentState {
  return { ...component, fields: component.fields.map(update) };
}

function findField(component: ComponentState, key: string): FldListState | undefined {
  return component.fields.find((f) => f.key === key);
}

/** Flutter setCurrentExpectedSalary — expected salary is INR for India, USD otherwise. */
function withExpectedSalaryCurrency(app: ParsedApplication, isIndia: boolean): ParsedApplication {
  const section: SectionKey = isIndia ? 'professionalDetails' : 'professionalDetailsNonIndia';
  return withComponent(app, section, undefined, (c) =>
    withFields(c, (f) => (f.key === 'currentExpectedSalary' ? { ...f, currencyCode: isIndia ? 'INR' : 'USD' } : f)),
  );
}

/** Flutter setCurrencyCode (current location → India: INR on the salary fields, else USD on expected salary). */
function withLocationCurrency(app: ParsedApplication, countryName: string): ParsedApplication {
  if (countryName.trim().toLowerCase() === 'india') {
    return withComponent(app, 'professionalDetails', undefined, (c) =>
      withFields(c, (f) => (SALARY_KEYS.includes(f.key) ? { ...f, currencyCode: 'INR' } : f)),
    );
  }
  return withComponent(app, 'professionalDetailsNonIndia', undefined, (c) =>
    withFields(c, (f) => (f.key === 'currentExpectedSalary' ? { ...f, currencyCode: 'USD' } : f)),
  );
}

/** Flutter _UpdateAdditionalDetails — fields whose `disableByKey` points at `key` follow its "no" answer. */
function applyDisableByKey(app: ParsedApplication, key: string): ParsedApplication {
  const controller = findField(app.additionalInfo, key);
  if (!controller) return app;
  const isNo = (controller.value ?? '').toLowerCase() === 'no';
  return withComponent(app, 'additionalInfo', undefined, (c) =>
    withFields(c, (f) => {
      if (f.disableByKey !== key) return f;
      return isNo
        ? { ...f, isDisabled: true, isMandatory: false, value: '', displayValue: '', isInValid: false }
        : { ...f, isDisabled: false, isMandatory: f.isMandatoryForLocal };
    }),
  );
}

/** Flutter _SetMasterDataForAddInfo — "disclaimer signed" is today's date. */
function withDisclaimerDate(app: ParsedApplication): ParsedApplication {
  const now = new Date();
  return withComponent(app, 'additionalInfo', undefined, (c) =>
    withFields(c, (f) => {
      if (f.key !== 'disclaimerSigned') return f;
      return f.type === FieldType.DATE
        ? { ...f, value: String(now.getTime()), displayValue: formatDateForDisplay(now) }
        : { ...f, value: formatDateForDisplay(now) };
    }),
  );
}

/**
 * Flutter _UpdateForm4Flds — on reaching Additional Information: relocation is required when the
 * job location differs from the value of the first address field (the current location option
 * key), and "no" answers disable their dependants.
 */
function prepareAdditionalInfo(app: ParsedApplication, jobLocation: string): ParsedApplication {
  const currentLocationValue = app.address.fields[0]?.value ?? '';
  let next = withComponent(app, 'additionalInfo', undefined, (c) =>
    withFields(c, (f) =>
      f.key === 'relocation'
        ? { ...f, isMandatory: !!jobLocation && !!currentLocationValue && jobLocation !== currentLocationValue }
        : f,
    ),
  );
  for (const f of next.additionalInfo.fields) {
    if ((f.value ?? '').toLowerCase() === 'no') next = applyDisableByKey(next, f.key);
  }
  return next;
}

/** Flutter _totalCurrentGrossAnnualSalary — gross = fixed + variable. */
function withGrossSalary(app: ParsedApplication): ParsedApplication {
  const fields = app.professionalDetails.fields;
  const num = (key: string) => parseInt(findField(app.professionalDetails, key)?.value || '0', 10) || 0;
  const total = num('currentFixedSalary') + num('currentVariableSalary');
  if (!fields.some((f) => f.key === 'currentGrossSalary')) return app;
  return withComponent(app, 'professionalDetails', undefined, (c) =>
    withFields(c, (f) => (f.key === 'currentGrossSalary' ? { ...f, value: total > 0 ? String(total) : '' } : f)),
  );
}

function newEntry(template: Record<string, unknown> | undefined): ComponentState {
  return template ? parseComponentJson(template) : { headerTitle: '', isHighestQualification: false, fields: [] };
}

/**
 * Flutter _PopulateData — fill the form from a draft, master data or resume extraction.
 * Returns null when there is nothing to populate.
 */
function populateApplication(
  app: ParsedApplication,
  raw: unknown,
  templates: Templates,
): { application: ParsedApplication; resumeUrl: string; isIndia: boolean } | null {
  if (!raw || typeof raw !== 'object' || Object.keys(raw as object).length === 0) return null;
  const data = raw as Record<string, unknown>;
  const pi = (data.personalInformation ?? {}) as Record<string, unknown>;
  const prof = data.professionalDetails as Record<string, unknown> | undefined;
  let next: ParsedApplication = { ...app };

  // Personal information
  next.personalInformation = populateComponentFields(app.personalInformation, pi);

  // Total experience → the years / months pair of both professional lists
  if (pi.experienceInMonths != null && pi.experienceInMonths !== '') {
    const [years, months] = getMonthsToYearArray(Number(pi.experienceInMonths));
    for (const section of ['professionalDetails', 'professionalDetailsNonIndia'] as const) {
      next = withComponent(next, section, undefined, (c) =>
        withFields(c, (f, i) => (i === 0 ? { ...f, value: years } : i === 1 ? { ...f, value: months } : f)),
      );
    }
  }

  // Address (nationality is stored on personalInformation)
  let isIndia = true;
  const addressData = Array.isArray(pi.address) ? (pi.address[0] as Record<string, unknown> | undefined) : undefined;
  if (addressData) {
    const populated = populateComponentFields(app.address, addressData);
    next.address = withFields(populated, (f) => {
      if (f.key === 'nationality') {
        const value = pi.nationality != null ? String(pi.nationality) : '';
        if (value.toLowerCase() !== 'india') isIndia = false;
        return { ...f, value, displayValue: '' };
      }
      if (f.key === 'country') {
        isIndia = !(f.value && f.value.trim().toLowerCase() !== 'india');
      }
      return f;
    });
    next = withExpectedSalaryCurrency(next, isIndia);
  }

  // Professional details: notice period from personalInformation, salaries with their currency, dates.
  // Kept broader than Flutter, which fills only those: the other professional fields are filled too
  const profSection: SectionKey = isIndia ? 'professionalDetails' : 'professionalDetailsNonIndia';
  next = withComponent(next, profSection, undefined, (c) =>
    withFields(c, (f) => {
      if (f.subType === 'EXP') return f;
      if (f.key === 'noticePeriodInDays') {
        return pi.noticePeriodInDays != null ? { ...f, value: String(pi.noticePeriodInDays) } : f;
      }
      if (!prof) return f;
      if (SALARY_KEYS.includes(f.key)) {
        return {
          ...f,
          currencyCode: (prof.currencyCode as string) || (isIndia ? 'INR' : 'USD'),
          value: prof[f.key] != null ? String(prof[f.key]) : '',
        };
      }
      return populateComponentFields({ headerTitle: '', isHighestQualification: false, fields: [f] }, prof).fields[0];
    }),
  );

  // Education — one entry per record, otherwise keep one empty entry
  const eduData = Array.isArray(data.education) ? (data.education as Record<string, unknown>[]) : [];
  if (eduData.length > 0) {
    next.education = eduData.map((entry) => ({
      ...populateComponentFields(newEntry(templates.education), entry),
      isHighestQualification: !!entry.isHighestDegree,
    }));
  } else if (next.education.length === 0) {
    next.education = [newEntry(templates.education)];
  }

  // Experience — current company disables the end date
  const expData = Array.isArray(data.experience) ? (data.experience as Record<string, unknown>[]) : [];
  if (expData.length > 0) {
    next.experience = expData.map((entry) => {
      const isCurrent = !!entry.isCurrentCompany;
      const populated = populateComponentFields(newEntry(templates.experience), entry);
      return withFields(populated, (f) =>
        f.key === 'endDate'
          ? { ...f, isCurrentCompany: isCurrent, isDisabled: isCurrent, value: isCurrent ? '' : f.value, displayValue: isCurrent ? '' : f.displayValue }
          : { ...f, isCurrentCompany: isCurrent },
      );
    });
  }

  // Additional information
  if (data.additionalInfo && typeof data.additionalInfo === 'object') {
    next.additionalInfo = populateComponentFields(app.additionalInfo, data.additionalInfo as Record<string, unknown>);
  }

  // Questionnaire answers (saved as [{ question, answer }]). D2h: Flutter doesn't restore them
  if (Array.isArray(data.questionInfo)) {
    const answers = new Map(
      (data.questionInfo as Array<{ question?: string; answer?: unknown }>).map((q) => [q.question, q.answer]),
    );
    next.questionInfo = withFields(app.questionInfo, (f) =>
      answers.get(f.title) != null && answers.get(f.title) !== '' ? { ...f, value: String(answers.get(f.title)) } : f,
    );
  }

  const resumeUrl = typeof pi.candidateUrl === 'string' ? pi.candidateUrl : '';
  return { application: next, resumeUrl, isIndia };
}

/** Flutter setUpdatedValues — the application payload. */
function buildPayload(s: ApplicationFormState): Record<string, unknown> {
  const app = s.application;
  const payload: Record<string, unknown> = {
    jobsInfo: {
      id: s.profileInput.jobId ?? '',
      displayId: s.profileInput.jobDisplayId,
      jobTitle: s.profileInput.jobTitle,
    },
    appliedStatus: s.appliedStatus || ApplicationStatus.DRAFT,
  };
  if (!app) return payload;

  const personal: Record<string, unknown> = {
    candidateUrl: s.resumeUrl,
    primaryEmailId: s.primaryEmailId,
  };
  for (const f of app.personalInformation.fields) {
    if (f.key && f.key !== 'experienceInMonths') personal[f.key] = fieldPayloadValue(f);
  }

  const address: Record<string, unknown> = {};
  for (const f of app.address.fields) {
    if (!f.key) continue;
    if (f.key === 'nationality') personal.nationality = fieldPayloadValue(f);
    else address[f.key] = fieldPayloadValue(f);
  }
  personal.address = [address];

  const professional: Record<string, unknown> = {};
  const profFields = s.isIndia ? app.professionalDetails.fields : app.professionalDetailsNonIndia.fields;
  for (const f of profFields) {
    if (!f.key || f.subType === 'EXP') continue;
    if (SALARY_KEYS.includes(f.key)) {
      professional.currencyCode = f.currencyCode;
      professional[f.key] = f.value ?? '';
    } else if (f.key === 'noticePeriodInDays') {
      personal.noticePeriodInDays = f.value ?? '';
    } else {
      professional[f.key] = fieldPayloadValue(f);
    }
  }

  // Total experience comes from the years / months pair (always the India list, as in Flutter)
  const years = findField(app.professionalDetails, 'experienceInYears');
  if (years?.subType === 'EXP') {
    const idx = app.professionalDetails.fields.indexOf(years);
    const months = app.professionalDetails.fields[idx + 1];
    personal.experienceInMonths = calculateExperienceInMonths(years.value || '0', months?.value || '0');
  }

  payload.personalInformation = personal;
  payload.professionalDetails = professional;
  payload.education = app.education.map((entry) => {
    const json: Record<string, unknown> = {};
    if (entry.isHighestQualification) json.isHighestDegree = true;
    for (const f of entry.fields) if (f.key) json[f.key] = fieldPayloadValue(f);
    return json;
  });
  payload.experience = app.experience.map((entry) => {
    const json: Record<string, unknown> = {};
    for (const f of entry.fields) {
      if (!f.key) continue;
      if (f.key === 'endDate' && f.isCurrentCompany) json.isCurrentCompany = true;
      json[f.key] = fieldPayloadValue(f);
    }
    return json;
  });
  const additional: Record<string, unknown> = {};
  for (const f of app.additionalInfo.fields) if (f.key) additional[f.key] = fieldPayloadValue(f);
  payload.additionalInfo = additional;
  payload.questionInfo = app.questionInfo.fields.map((f) => ({ question: f.title, answer: f.value ?? '' }));
  return payload;
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

/* ────────────────────────────── Hook ────────────────────────────── */

export function useApplicationForm() {
  // Flutter routing_config: /application and /easyApplyApplication call setDefaultApplicationOrigin()
  useEffect(() => {
    setDefaultApplicationOrigin();
  }, []);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id: jobDisplayId } = useParams<{ id: string }>();
  const preferences = useTenantStore((s) => s.preferences);
  const tenantConfig = useTenantStore((s) => s.config);
  const currentLanguage = useLocalizationStore((s) => s.currentLanguage);

  const [state, setState] = useState<ApplicationFormState>({
    isInitializing: true,
    isSaving: false,
    isUploading: false,
    activeStep: STEPS.RESUME,
    application: null,
    profileInput: { jobDisplayId: jobDisplayId ?? '' },
    candidateId: '',
    primaryEmailId: '',
    resumeUrl: '',
    resumeUploaded: false,
    selectedFile: null,
    uploadProgress: 0,
    isLoadingResumeData: true,
    isIndia: true,
    selectedCountry: '',
    appliedStatus: '',
    showContinuePopup: false,
    consentChecked: false,
    errors: {},
    toast: null,
    showQuestionInfo: false,
  });

  // Read live — the tenant preferences may load after the page mounts
  const showQuestionInfo = (preferences as Record<string, unknown> | null)?.questionInfo != null;
  const stateRef = useRef(state);
  stateRef.current = { ...state, showQuestionInfo };
  const templatesRef = useRef<Templates>({});
  const busyRef = useRef(false);

  const patch = useCallback((partial: Partial<ApplicationFormState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  /** Flutter CustomFlushBar — `message` is Flutter's English text; Toast translates it. */
  const showToast = useCallback(
    (message: string, type: Toast['type'] = 'error') => patch({ toast: { message, type } }),
    [patch],
  );

  /** Apply a populate result (draft, master data or resume auto-fill). */
  const applyPopulate = useCallback((raw: unknown) => {
    setState((prev) => {
      // Flutter _PopulateData first ends the resume shimmer (isLoadingForResumeData: false)
      if (!prev.application) return { ...prev, isLoadingResumeData: false };
      const result = populateApplication(prev.application, raw, templatesRef.current);
      if (!result) return { ...prev, isLoadingResumeData: false };
      return {
        ...prev,
        application: result.application,
        isIndia: result.isIndia,
        isLoadingResumeData: false,
        ...(result.resumeUrl ? { resumeUrl: result.resumeUrl, resumeUploaded: true } : {}),
      };
    });
  }, []);

  /* ── Language change reloads the page (Flutter languageStream → window.location.reload) ── */
  const languageRef = useRef(currentLanguage);
  useEffect(() => {
    if (languageRef.current !== currentLanguage) window.location.reload();
  }, [currentLanguage]);

  /*
   * ── Init — Flutter _CandidateAplicationFormInit → _GetJobDetailsById → _Init ──
   * Signed-out users never get here: ProtectedRoute sends them to /login and back.
   */
  useEffect(() => {
    // Flutter only loads the job when there is an id; without one the empty form is shown
    if (!jobDisplayId) {
      patch({ isInitializing: false });
      return;
    }
    let cancelled = false;

    (async () => {
      const cachedForm = (() => {
        try {
          return JSON.parse(localStorage.getItem('applyFormContent') ?? 'null');
        } catch {
          return null;
        }
      })();

      const [formJson, jobData, candidate] = await Promise.all([
        getApplyApplicationFormContent().catch(() => cachedForm),
        getJobDetails(jobDisplayId).catch(() => null),
        (async () => useAppStateStore.getState().candidateDetails ?? (await getUserDetails().catch(() => null)))(),
      ]);
      if (cancelled) return;

      if (!jobData) {
        showToast(GENERIC_ERROR);
        setTimeout(() => {
          void analyticsService.logScreenView({ screenName: ROUTES.HOME });
          navigate(ROUTES.HOME, { replace: true });
        }, 1000);
        return;
      }

      const job = jobData as Record<string, unknown>;
      const locations = (job.jobLocation as Array<{ fqLocationName?: string; country?: string }> | undefined) ?? [];
      const skills = ((job.skills as Array<{ skill?: string }> | undefined) ?? [])
        .map((s) => s.skill)
        .filter(Boolean)
        .join(', ');
      const profileInput: ProfileInput = {
        clickedOn: ClickOptions.APPLIED,
        profileTabIndex: -1,
        tabIndex: 4,
        skills,
        jobDisplayId: (job.displayId as string) ?? jobDisplayId,
        jobTitle: (job.jobTitle as string) ?? '',
        jobLocation: locations[0]?.fqLocationName ?? '',
        jobId: (job.id as string) ?? '',
      };

      const form = formJson as Record<string, unknown> | null;
      templatesRef.current = {
        education: (form?.education as Record<string, unknown>[] | undefined)?.[0],
        experience: (form?.experience as Record<string, unknown>[] | undefined)?.[0],
      };
      const parsed = parseApplicationFormJson(form as never);
      if (parsed) {
        parsed.education.pop();
        parsed.experience.pop();
        applyGenderMandatoryInCountries(parsed, locations[0]?.country ?? '', useTenantStore.getState().preferences);
      }

      const c = (candidate ?? {}) as Record<string, unknown>;
      setState((prev) => ({
        ...prev,
        isInitializing: false,
        application: parsed,
        profileInput,
        candidateId: (c.id as string) ?? '',
        primaryEmailId: (c.primaryEmailId as string) ?? '',
      }));

      // Draft for this job, otherwise master data (Flutter _GetPartialApplnFormData → _GetMasterDetails)
      let draft: unknown = null;
      try {
        draft = await getPartialApplicationFormData(jobDisplayId);
      } catch {
        draft = null;
      }
      if (cancelled) return;
      const draftData = draft && typeof draft === 'object' ? (draft as Record<string, unknown>) : null;
      // An empty draft (`200 {}`) also falls back to master data; Flutter would populate nothing.
      // Kept until the backend's response is confirmed
      if (draftData && Object.keys(draftData).length > 0) {
        const status = (draftData.appliedStatus as string) ?? '';
        setState((prev) => ({
          ...prev,
          profileInput: { ...prev.profileInput, applicationId: (draftData.applicationId as string) ?? '' },
          appliedStatus: status,
          showContinuePopup: status === ApplicationStatus.APPLIED,
        }));
        applyPopulate(draftData);
        return;
      }
      const master = await getMasterData().catch(() => null);
      if (!cancelled) applyPopulate(master);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDisplayId]);

  /* ── Field updates ── */

  const clearError = (errors: Record<string, string>, ref: FieldRef) => {
    const key = errorKey(ref);
    if (!(key in errors)) return errors;
    const next = { ...errors };
    delete next[key];
    return next;
  };

  /** Flutter _UpdateStateCity — address cascades (country → states, state → cities, current location → all). */
  const handleAddressChange = useCallback(async (fieldKey: string, value: string) => {
    if (fieldKey === 'country') {
      const isIndia = value.toLowerCase() === 'india';
      setState((prev) => {
        if (!prev.application) return prev;
        const address = withFields(prev.application.address, (f) =>
          f.key === 'state' || f.key === 'city' ? { ...f, value: '', displayValue: '' } : f,
        );
        return { ...prev, isIndia, selectedCountry: value, application: { ...prev.application, address } };
      });
      const states = await getPlaceSuggestions({ placeType: 'state', country: value }).catch(() => null);
      if (!states) return;
      setState((prev) => {
        if (!prev.application) return prev;
        const address = withFields(prev.application.address, (f) =>
          f.key === 'state' ? { ...f, dropDownOptions: states } : f,
        );
        return { ...prev, application: { ...prev.application, address } };
      });
    } else if (fieldKey === 'state') {
      // D2f: Flutter's inverted selectedCountry check sends country '' once a country was picked;
      // the chosen (or prefilled) country is sent here instead
      const s = stateRef.current;
      const country = s.selectedCountry || (s.application ? findField(s.application.address, 'country')?.value : '') || '';
      setState((prev) => {
        if (!prev.application) return prev;
        const address = withFields(prev.application.address, (f) =>
          f.key === 'city' ? { ...f, value: '', displayValue: '' } : f,
        );
        return { ...prev, application: { ...prev.application, address } };
      });
      const cities = await getPlaceSuggestions({ placeType: 'city', country, state: value }).catch(() => null);
      if (!cities) return;
      setState((prev) => {
        if (!prev.application) return prev;
        const address = withFields(prev.application.address, (f) =>
          f.key === 'city' ? { ...f, dropDownOptions: cities } : f,
        );
        return { ...prev, application: { ...prev.application, address } };
      });
    } else if (fieldKey === 'currentLocation') {
      // Flutter splits the field value — the option key — into city, state and country
      const parts = value.split(',').map((p) => p.trim());
      if (parts.length < 2) return;
      const [city, stateName, country] = parts.length === 2 ? ['', parts[0], parts[1]] : [parts[0], parts[1], parts[2]];
      const last = parts[parts.length - 1];
      setState((prev) => {
        if (!prev.application) return prev;
        const values: Record<string, string> = { city, state: stateName, country };
        let app: ParsedApplication = {
          ...prev.application,
          address: withFields(prev.application.address, (f) =>
            f.key in values ? { ...f, value: values[f.key], displayValue: values[f.key] } : f,
          ),
        };
        app = withLocationCurrency(app, last);
        const errors = { ...prev.errors };
        prev.application.address.fields.forEach((f, index) => {
          if (f.key in values) delete errors[errorKey({ section: 'address', index })];
        });
        return { ...prev, application: app, isIndia: last.toLowerCase() === 'india', errors };
      });
    }
  }, []);

  const updateField = useCallback(
    (ref: FieldRef, value: string, displayValue?: string) => {
      setState((prev) => {
        if (!prev.application) return prev;
        let app = withComponent(prev.application, ref.section, ref.entry, (c) =>
          withFields(c, (f, i) =>
            i === ref.index ? { ...f, value, displayValue: displayValue ?? (value ? f.displayValue : '') } : f,
          ),
        );
        const changed = getComponent(app, ref.section, ref.entry).fields[ref.index];
        if (ref.section === 'professionalDetails' && SALARY_KEYS.includes(changed?.key ?? '')) {
          app = withGrossSalary(app);
        }
        if (ref.section === 'additionalInfo' && changed?.key) {
          app = applyDisableByKey(app, changed.key);
        }
        return { ...prev, application: app, errors: clearError(prev.errors, ref) };
      });
      if (ref.section === 'address') {
        const field = stateRef.current.application?.address.fields[ref.index];
        if (field) void handleAddressChange(field.key, value);
      }
    },
    [handleAddressChange],
  );

  const updateCountryCode = useCallback((ref: FieldRef, code: string) => {
    setState((prev) => {
      if (!prev.application) return prev;
      const app = withComponent(prev.application, ref.section, ref.entry, (c) =>
        withFields(c, (f, i) => (i === ref.index ? { ...f, countryCode: code } : f)),
      );
      return { ...prev, application: app };
    });
  }, []);

  /** Flutter currency picker — the chosen currency applies to every salary field of the list. */
  const updateCurrency = useCallback((section: SectionKey, code: string) => {
    setState((prev) => {
      if (!prev.application) return prev;
      const app = withComponent(prev.application, section, undefined, (c) =>
        withFields(c, (f) => ({ ...f, currencyCode: code })),
      );
      return { ...prev, application: app };
    });
  }, []);

  /** Flutter _SetCurrentCompanyData — only one current company; its end date is cleared and disabled. */
  const toggleCurrentCompany = useCallback((entry: number) => {
    setState((prev) => {
      if (!prev.application) return prev;
      const wasCurrent = !!findField(prev.application.experience[entry], 'endDate')?.isCurrentCompany;
      const experience = prev.application.experience.map((exp, i) =>
        withFields(exp, (f) => {
          if (f.key !== 'endDate') return f;
          if (i !== entry) return { ...f, isCurrentCompany: false, isDisabled: false };
          const isCurrent = !wasCurrent;
          return isCurrent
            ? { ...f, isCurrentCompany: true, isDisabled: true, value: '', displayValue: '' }
            : { ...f, isCurrentCompany: false, isDisabled: false };
        }),
      );
      return { ...prev, application: { ...prev.application, experience } };
    });
  }, []);

  /** Flutter _SetHighestQualification — one highest qualification. */
  const setHighestQualification = useCallback((entry: number) => {
    setState((prev) => {
      if (!prev.application) return prev;
      const education = prev.application.education.map((edu, i) => ({
        ...edu,
        isHighestQualification: i === entry ? !edu.isHighestQualification : false,
      }));
      return { ...prev, application: { ...prev.application, education } };
    });
  }, []);

  /** Flutter _OnClickAdd */
  const addEntry = useCallback((section: 'education' | 'experience') => {
    setState((prev) => {
      if (!prev.application || prev.application[section].length >= MAX_REPEATING_ENTRIES) return prev;
      const list = [...prev.application[section], newEntry(templatesRef.current[section])];
      return { ...prev, application: { ...prev.application, [section]: list } };
    });
  }, []);

  /** Flutter _OnClickRemove — removes the last entry (education keeps at least one). */
  const removeLastEntry = useCallback((section: 'education' | 'experience') => {
    setState((prev) => {
      if (!prev.application) return prev;
      const list = prev.application[section];
      if (list.length === 0 || (section === 'education' && list.length <= 1)) return prev;
      return { ...prev, application: { ...prev.application, [section]: list.slice(0, -1) } };
    });
  }, []);

  const setConsent = useCallback((checked: boolean) => patch({ consentChecked: checked }), [patch]);

  /* ── Resume ── */

  /** Flutter _SelectFilesFromSystem → _UploadResume (+ _FetchMasterDataFromResume when auto-fill is on). */
  const selectResume = useCallback(
    async (file: File) => {
      const fileError = validateResumeFile(file);
      if (fileError) {
        showToast(fileError);
        return;
      }
      const s = stateRef.current;
      patch({ isUploading: true, selectedFile: file, uploadProgress: 0 });

      if ((tenantConfig as Record<string, unknown> | null)?.enableAutoFillByUploadResume) {
        fetchMasterDataFromResume(file).then(applyPopulate).catch(() => {});
      }

      const referral = getJobReferralData();
      try {
        const result = await uploadResumeForApplication(
          file,
          s.candidateId,
          s.profileInput.jobId ?? '',
          { channel: referral.channel ?? '', sourceName: referral.sourceName ?? '' },
          { ...getApplicationPageFields(), applyType: 'APPLY' },
          (pct) => patch({ uploadProgress: pct }),
        );
        setState((prev) => ({
          ...prev,
          isUploading: false,
          resumeUrl: file.name,
          resumeUploaded: true,
          uploadProgress: 100,
          profileInput: { ...prev.profileInput, applicationId: (result?.key as string) ?? prev.profileInput.applicationId },
        }));
        void analyticsService.logEvent('Apply Job Form', 'uploaded_resume');
      } catch (err) {
        // Flutter: resumeUploadStatus false + the repository's message
        patch({ isUploading: false, uploadProgress: 0, selectedFile: null, resumeUrl: '', resumeUploaded: false });
        showToast(uploadFailureMessage(err));
      }
    },
    [applyPopulate, patch, showToast, tenantConfig],
  );

  const downloadCurrentResume = useCallback(async () => {
    try {
      const { data, fileName } = await downloadResume();
      const url = URL.createObjectURL(data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName.replace(/"$/, '');
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      void analyticsService.logEvent('Apply Job Form', 'download_resume');
    } catch {
      // Flutter _DownloadResume ignores a failed download
    }
  }, []);

  const deleteResume = useCallback(() => {
    patch({ resumeUrl: '', resumeUploaded: false, selectedFile: null, uploadProgress: 0 });
  }, [patch]);

  /* ── Navigation between steps ── */

  /** Steps before the current one can be revisited (Flutter _customStep onTap). */
  const goToStep = useCallback((step: number) => {
    if (step < stateRef.current.activeStep) {
      patch({ activeStep: step, errors: {} });
      scrollToTop();
    }
  }, [patch]);

  /** Flutter WebResume "Save & Continue" — create the application, update the draft, or just move on. */
  const continueFromResume = useCallback(async () => {
    const s = stateRef.current;
    if (!s.resumeUploaded || busyRef.current) return;
    void analyticsService.logEvent('Apply Job Form - Resume', 'click_save_and_continue');
    const applicationId = s.profileInput.applicationId;

    if (!s.selectedFile && !applicationId && s.profileInput.clickedOn === ClickOptions.APPLIED) {
      const referral = getJobReferralData();
      await save(
        { ...buildPayload(s), ...getApplicationPageFields(), ...(referral.channel || referral.sourceName ? referral : {}) },
        STEPS.PERSONAL_INFO,
      );
    } else if (applicationId) {
      await save({ ...buildPayload(s), ...getApplicationPageFields() }, STEPS.PERSONAL_INFO);
    } else {
      patch({ activeStep: STEPS.PERSONAL_INFO });
      scrollToTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patch]);

  /**
   * POST / PUT the application and move to `nextStep`, or, on the final step, leave for
   * `finishRoute`. A failed save shows nothing, as in Flutter (isFailed without showMessage).
   */
  const save = async (payload: Record<string, unknown>, nextStep: number, finishRoute?: string) => {
    const s = stateRef.current;
    busyRef.current = true;
    patch({ isSaving: true });
    try {
      const applicationId = await saveApplication(
        s.candidateId,
        s.profileInput.jobId ?? '',
        s.profileInput.applicationId,
        payload,
      );
      if (finishRoute) {
        // Flutter navigateTo(CoreRoute.profile, …) logs screen_view for '/profile' either way
        void analyticsService.logScreenView({ screenName: ROUTES.PROFILE });
        navigate(finishRoute, { replace: true });
        return;
      }
      setState((prev) => {
        let app = prev.application;
        if (app && nextStep === STEPS.ADDITIONAL_INFO) {
          app = prepareAdditionalInfo(withDisclaimerDate(app), prev.profileInput.jobLocation ?? '');
        }
        return {
          ...prev,
          isSaving: false,
          activeStep: nextStep,
          application: app,
          errors: {},
          profileInput: { ...prev.profileInput, applicationId: applicationId || prev.profileInput.applicationId },
        };
      });
      scrollToTop();
    } catch {
      patch({ isSaving: false });
    } finally {
      busyRef.current = false;
    }
  };

  /* ── Validation per step (Flutter formKey.validate() + the step's own checks) ── */

  /** Errors by field, plus the dates Flutter's validator clears for being out of order. */
  const collectErrors = (refs: FieldRef[], app: ParsedApplication) => {
    const errors: Record<string, string> = {};
    const invalidDates: FieldRef[] = [];
    for (const ref of refs) {
      const component = getComponent(app, ref.section, ref.entry);
      const field = component.fields[ref.index];
      if (!field || field.isDisabled) continue;
      const ctx = validationContextFor(ref.section, component);
      const err = validateFormField(field, t, ctx);
      if (err) errors[errorKey(ref)] = err;
      if (isDateOrderError(field, ctx)) invalidDates.push(ref);
    }
    return { errors, invalidDates };
  };

  const clearFields = (refs: FieldRef[]) => {
    setState((prev) => {
      if (!prev.application) return prev;
      let app = prev.application;
      for (const ref of refs) {
        app = withComponent(app, ref.section, ref.entry, (c) =>
          withFields(c, (f, i) => (i === ref.index ? { ...f, value: '', displayValue: '' } : f)),
        );
      }
      return { ...prev, application: app };
    });
  };

  const refsOf = (app: ParsedApplication, section: SectionKey, from = 0, entry?: number): FieldRef[] =>
    getComponent(app, section, entry)
      .fields.map((_, index) => ({ section, index, entry }))
      .slice(from);

  /** Personal information step fields in render order (the EXP pair always comes from the India list). */
  const personalStepRefs = (app: ParsedApplication, isIndia: boolean): FieldRef[] => {
    const hasExpPair = app.professionalDetails.fields[0]?.subType === 'EXP';
    return [
      ...refsOf(app, 'personalInformation'),
      ...refsOf(app, 'address'),
      ...(hasExpPair ? refsOf(app, 'professionalDetails').slice(0, 2) : []),
      ...refsOf(app, isIndia ? 'professionalDetails' : 'professionalDetailsNonIndia', 2),
    ];
  };

  /**
   * Flutter "Save & Continue" of steps 1–4 (formKey.validate() + the step's own checks, then
   * onClickSaveContinue). The final step submits (APPLIED) and leaves the form: after the
   * questionnaire for /profile (WebIntegrityQuestionnaire), after Additional Information for
   * My Jobs › Applied (WebAboutTataCommunications: /profile with profileTabIndex 1).
   */
  const saveAndContinue = useCallback(async () => {
    const s = stateRef.current;
    const app = s.application;
    if (!app || busyRef.current) return;

    let refs: FieldRef[] = [];
    let extraCheck: string | null = null;
    let consentRequired = false;
    /** Logged once the step validates (Flutter logs it inside `if (formKey.validate())`). */
    let validatedEvent = '';

    switch (s.activeStep) {
      case STEPS.PERSONAL_INFO:
        refs = personalStepRefs(app, s.isIndia);
        validatedEvent = 'Apply Job Form - Personal Information';
        break;
      case STEPS.EDUCATION_EXPERIENCE:
        // Flutter logs this on every click, before validating
        void analyticsService.logEvent('Apply Job Form - Education & Experience', 'click_save_and_continue');
        refs = [
          ...app.education.flatMap((_, entry) => refsOf(app, 'education', 0, entry)),
          ...app.experience.flatMap((_, entry) => refsOf(app, 'experience', 0, entry)),
        ];
        if (!app.education.some((e) => e.isHighestQualification)) extraCheck = FILL_ALL_MANDATORY;
        break;
      case STEPS.ADDITIONAL_INFO:
        refs = refsOf(app, 'additionalInfo');
        consentRequired = !s.showQuestionInfo;
        validatedEvent = 'Apply Job Form - About company';
        break;
      case STEPS.QUESTION_INFO: {
        // D2h: only visible questions must be answered (Flutter also blocks on hidden ones)
        const unanswered = app.questionInfo.fields.some(
          (f) => isQuestionVisible(f, app.questionInfo.fields) && !(f.value ?? '').toString().trim(),
        );
        if (unanswered) extraCheck = FILL_ALL_MANDATORY;
        consentRequired = true;
        break;
      }
      default:
        return;
    }

    const { errors, invalidDates } = collectErrors(refs, app);
    if (invalidDates.length > 0) clearFields(invalidDates);
    if (Object.keys(errors).length > 0 || extraCheck) {
      patch({ errors });
      showToast(FILL_ALL_MANDATORY);
      return;
    }
    if (validatedEvent) void analyticsService.logEvent(validatedEvent, 'click_save_and_continue');
    if (consentRequired && !s.consentChecked) {
      showToast(CONSENT_REQUIRED);
      return;
    }

    const payload: Record<string, unknown> = { ...buildPayload(s), ...getApplicationPageFields() };
    if (s.activeStep === STEPS.QUESTION_INFO) {
      payload.appliedStatus = ApplicationStatus.APPLIED;
      // Flutter's questionnaire step goes to the Profile tab (WebIntegrityQuestionnaire:
      // navigateTo(CoreRoute.profile) without profileTabIndex). React lands on My Jobs → Applied,
      // like every other submit (doc/FLUTTER_DEVIATIONS.md D2j).
      await save(payload, s.activeStep, ROUTES.VIEW_JOBS_APPLIED);
    } else if (s.activeStep === STEPS.ADDITIONAL_INFO && !s.showQuestionInfo) {
      payload.appliedStatus = ApplicationStatus.APPLIED;
      await save(payload, s.activeStep, ROUTES.VIEW_JOBS_APPLIED);
    } else {
      await save(payload, s.activeStep + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patch, showToast, t]);

  /** Flutter confirmEditAppliedJob dialog — cancelling goes back home. */
  const dismissContinuePopup = useCallback(
    (proceed: boolean) => {
      patch({ showContinuePopup: false });
      if (!proceed) {
        void analyticsService.logScreenView({ screenName: ROUTES.HOME });
        navigate(ROUTES.HOME, { replace: true });
      }
    },
    [navigate, patch],
  );

  const clearToast = useCallback(() => patch({ toast: null }), [patch]);

  return {
    state: { ...state, showQuestionInfo },
    actions: {
      updateField,
      updateCountryCode,
      updateCurrency,
      toggleCurrentCompany,
      setHighestQualification,
      addEntry,
      removeLastEntry,
      setConsent,
      selectResume,
      downloadResume: downloadCurrentResume,
      deleteResume,
      goToStep,
      continueFromResume,
      saveAndContinue,
      dismissContinuePopup,
      clearToast,
    },
  };
}
